import styled, { css } from 'styled-components';
import { theme } from '@frameio/vapor';
import { IconChevronRight } from '@frameio/vapor-icons';
import type { InviteAccessMemberProps } from './types';

type ComponentColorKey = keyof (typeof theme)['colors']['components'];
type ComponentsWithBackground = {
  [K in ComponentColorKey]: (typeof theme)['colors']['components'][K] extends {
    background: string;
  }
    ? K
    : never;
}[ComponentColorKey];
type BackgroundColorKey = keyof (typeof theme)['colors']['background'];

function getBackgroundColor(
  backgroundColor: InviteAccessMemberProps['backgroundColor']
): string {
  if (
    backgroundColor !== undefined &&
    backgroundColor in theme.colors.components
  ) {
    const componentColor =
      theme.colors.components[backgroundColor as ComponentsWithBackground];
    if (componentColor && 'background' in componentColor) {
      return (componentColor as { background: string }).background;
    }
  }
  return backgroundColor !== undefined
    ? (theme.colors.background[backgroundColor as BackgroundColorKey] as string)
    : '';
}

export const TRANSITION_DURATION = 0.2;

export const withBackgroundTransition = css`
  transition: background-color ${TRANSITION_DURATION}s
    ${theme.transitions.eases.primary};
  will-change: background-color;
`;

export const withOpacityTransition = css`
  transition: opacity 0.3s ${theme.transitions.eases.primary};
  will-change: opacity;
`;

export const StyledDivForTitle = styled.div<{
  $backgroundColor?: InviteAccessMemberProps['backgroundColor'];
  $showShadow?: boolean;
}>`
  align-items: center;
  background: ${(p) => getBackgroundColor(p.$backgroundColor)};
  box-shadow: ${(p) =>
    p.$showShadow ? '0px 0px 15px rgba(0, 0, 0, 0.3)' : undefined};
  display: flex;
  height: ${theme.spaces[32]};
  padding: 0 ${theme.spaces[16]};
  position: sticky;
  top: 0;
  transition: box-shadow 0.1s ${theme.transitions.eases.primary};
  z-index: ${theme.zIndices.tooltip + 1};

  &::before {
    background: ${(p) => getBackgroundColor(p.$backgroundColor)};
    content: '';
    display: block;
    height: ${theme.spaces[8]};
    left: 0;
    position: absolute;
    top: calc(${theme.spaces[8]} * -1);
    width: 100%;
    z-index: ${theme.zIndices.resizer};
  }
`;

export const StyledList = styled.ul`
  list-style-type: none;

  // Gives padding for last item in list's hover styles to avoid being covered by a subsequent sticky header
  padding-bottom: 5px;
  width: 100%;
`;

export const StyledDivForAdornmentBefore = styled.div`
  align-items: center;
  display: flex;
  height: ${theme.spaces[24]};
  justify-content: center;
  position: relative;
  width: ${theme.spaces[24]};
`;

export const StyledDivForAdornmentTransitions = styled.div<{
  $centered?: boolean;
}>`
  ${withOpacityTransition}
  display: flex;
  opacity: 1;
  position: absolute;
  top: ${(p) => (p.$centered ? '50%' : undefined)};
  transform: ${(p) => (p.$centered ? 'translateY(-50%)' : undefined)};

  &[aria-hidden='true'] {
    opacity: 0;
  }
`;

export const StyledIconChevronRight = styled(IconChevronRight)<{
  $rotate: number;
}>`
  color: ${theme.colors.icon.interactive.hover};
  display: inline-block;
  flex-shrink: 0;
  transform: rotate(${(p) => p.$rotate}deg);
  transition: transform ${TRANSITION_DURATION}s
    ${theme.transitions.eases.primary};
  will-change: transform;
`;

export const StyledDivForPending = styled.div<{
  $showBackground?: boolean;
}>`
  ${withBackgroundTransition}
  align-items: center;
  background-color: ${(p) =>
    p.$showBackground
      ? theme.colors.background.interactive.disabled
      : 'transparent'};
  border-radius: 100%;
  display: flex;
  height: ${theme.spaces[24]};
  justify-content: center;
  width: ${theme.spaces[24]};
`;
